import { useState, useRef, useCallback } from 'react';
import { VOICE_MESSAGE_NAME_PREFIX } from '@avanta/shared/constants/chat';

export interface UseVoiceRecorderResult {
  isRecording: boolean;
  error: Error | undefined;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<File | undefined>;
  audioFile: File | null;
  recordingTime: string;
  resetRecording: () => void;
}

export const useVoiceRecorder = (): UseVoiceRecorderResult => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const [recordingSeconds, setRecordingSeconds] = useState<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioFileRef = useRef<File | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const startRecording = useCallback(async (): Promise<void> => {
    setError(undefined);
    try {
      const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      recorder.addEventListener('dataavailable', (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      });
      recorder.addEventListener('stop', () => {
        stream.getTracks().forEach(track => track.stop());
      });
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      intervalRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError(err as Error);
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<File | undefined> => {
    setError(undefined);
    try {
      if (!mediaRecorderRef.current) {
        throw new Error('No se está grabando actualmente.');
      }
      const recorder = mediaRecorderRef.current;
      await new Promise<void>(resolve => {
        recorder.addEventListener('stop', () => resolve());
        recorder.stop();
      });
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
      const file = new File([blob], `${VOICE_MESSAGE_NAME_PREFIX}-${Date.now()}.webm`, {
        type: blob.type,
      });
      audioFileRef.current = file;
      setIsRecording(false);
      return file;
    } catch (err) {
      setError(err as Error);
      return undefined;
    }
  }, []);

  const resetRecording = useCallback((): void => {
    setIsRecording(false);
    setRecordingSeconds(0);
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    audioFileRef.current = null;
  }, []);

  return {
    isRecording,
    error,
    startRecording,
    stopRecording,
    resetRecording,
    audioFile: audioFileRef.current,
    recordingTime: formatTime(recordingSeconds),
  };
};
